/**
 * Synchro des pieces jointes avec le serveur souverain, ciphertext-only.
 * Contrairement aux entrees (lot complet a chaque synchro), les pieces jointes
 * sont volumineuses : le serveur n'en liste que les metadonnees, et seules
 * celles qui different (`planRecordTransfer`) sont envoyees ou telechargees,
 * une par requete. Tout blob telecharge est authentifie sous la `vaultKey`
 * (contenu ET metadonnees) puis fusionne avec l'etat local : un serveur
 * malveillant ne peut ni forger, ni substituer, ni rejouer une ancienne version.
 */
import {
  isNewer,
  planRecordTransfer,
  type EncryptedVaultAttachment,
  type VaultAttachmentMetadata,
} from '@klavox/vault-model';
import type { VaultRepository } from './repository.js';
import { fetchAttachment, pushSync } from './sync-client.js';

export interface AttachmentSyncResult {
  readonly pushed: number;
  readonly fetched: number;
  /** Blobs refuses (alteres, substitues, d'un autre coffre). */
  readonly rejected: number;
}

export async function syncAttachments(input: {
  readonly baseUrl: string;
  readonly sessionToken: string;
  readonly repository: VaultRepository;
  /** Metadonnees listees par le serveur (non fiables). */
  readonly remote: readonly VaultAttachmentMetadata[];
  /** Ne retient que les pieces jointes authentiques sous la `vaultKey`. */
  readonly authenticate: (attachments: readonly EncryptedVaultAttachment[]) => readonly EncryptedVaultAttachment[];
}): Promise<AttachmentSyncResult> {
  // ponytail: charge tous les blobs locaux (<= quota, ~190 Mo) le temps de la synchro ; lister les
  // seules metadonnees (curseur IndexedDB) si la memoire des appareils modestes devient un probleme.
  const local = await input.repository.listAttachments();
  const plan = planRecordTransfer(local, input.remote);
  for (const attachment of plan.toPush) {
    await pushSync(input.baseUrl, input.sessionToken, { attachments: [attachment] });
  }
  const localById = new Map(local.map((attachment) => [attachment.id, attachment]));
  let fetched = 0;
  let rejected = 0;
  for (const stub of plan.toFetch) {
    const [authentic] = input.authenticate([await fetchAttachment(input.baseUrl, input.sessionToken, stub.id)]);
    if (authentic === undefined || authentic.id !== stub.id) {
      rejected += 1;
      continue;
    }
    const current = localById.get(authentic.id);
    if (current !== undefined && !isNewer(authentic, current)) continue;
    await input.repository.putAttachments([authentic]);
    fetched += 1;
  }
  return { pushed: plan.toPush.length, fetched, rejected };
}
